import React, { useState, useEffect } from "react";
import { Eye, EyeOff, User as UserIcon } from "lucide-react";
import "./UserModal.css";
import Combobox from "../../../components/Combobox/Combobox";
import ButtonClose from "../../../components/Buttons/ButtonClose/ButtonClose";
import ButtonDelete from "../../../components/Buttons/ButtonDelete/ButtonDelete";
import ButtonSave from "../../../components/Buttons/ButtonSave/ButtonSave";
import ButtonCancel from "../../../components/Buttons/ButtonCancel/ButtonCancel";
import { useTranslation } from "react-i18next";
import useUser from "../../../../hooks/useUser";

export default function UserModal({
  isOpen,
  onClose,
  user,
  onSave,
  onDelete,
  users,
  creditLimits = {},
  roleOptions = [],
}) {
  const { t } = useTranslation('userManagement');
  const { user: userCtx } = useUser();
  const currentUserOrganization =
    userCtx?.organization ||
    userCtx?.organization_id;

  const [showPassword, setShowPassword] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState(() => {
    return {
      name: "",
      email: "",
      username: "",
      password: "",
      role_id: user?.role_id?.id ?? "",
      organization_id:
        currentUserOrganization?.id || currentUserOrganization || "",
      is_active: true,
      date_joined: "",
      is_staff: false,
      creditLimit: 1000,
    };
  });

  // Actualiza el role_id si cambia el usuario (edición)
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        role_id: user.role_id?.id ?? "",
      }));
    }
  }, [user]);

  // Actualiza creditLimit automáticamente al cambiar el role_id
  useEffect(() => {
    if (creditLimits && formData.role_id) {
      const limit =
        creditLimits[formData.role_id] ?? creditLimits[String(formData.role_id)];
      if (limit != null) {
        setFormData((prev) => ({
          ...prev,
          creditLimit: limit,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.role_id]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  // Inicializar formData cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      if (user) {
        setFormData({
          name: user.name || "",
          email: user.email || "",
          username: user.username || "",
          password: "",
          role_id: user.role_id?.id || "",
          organization_id:
            currentUserOrganization?.id || currentUserOrganization || "",
          is_active: user.is_active !== undefined ? user.is_active : true,
          date_joined: user.date_joined || "",
          is_staff: user.is_staff === true,
          creditLimit: user.creditLimit || 1000,
        });
      } else {
        setFormData({
          name: "",
          email: "",
          username: "",
          password: "",
          role_id: "",
          organization_id:
            currentUserOrganization?.id || currentUserOrganization || "",
          is_active: true,
          date_joined: "",
          is_staff: false,
          creditLimit: 1000,
        });
      }
    }
  }, [user, isOpen, currentUserOrganization]);

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setPasswordError("");

    // Validación de confirm password solo al crear
    if (!user && formData.password !== confirmPassword) {
      setPasswordError(t('modal.errors.passwordMismatch'));
      return;
    }

    // Validación de email único solo al crear
    if (!user && users) {
      const emailLower = formData.email.trim().toLowerCase();
      const exists = users.some(
        (u) => u.email && u.email.trim().toLowerCase() === emailLower
      );
      if (exists) {
        setError(t('modal.errors.emailExists'));
        setIsSubmitting(false);
        return;
      }
    }

    try {
      // Usar la organización del usuario logueado
      const organizationId =
        currentUserOrganization?.id || currentUserOrganization || null;

      const dataToSave = {
        name: formData.name,
        email: formData.email,
        username: formData.username,
        role_id: formData.role_id,
        is_active: formData.is_active,
        // is_staff: formData.is_staff, // Commented out: Admin access now handled through role.have_admin_access
        creditLimit: formData.creditLimit,
        organization: organizationId,
      };

      // Solo incluir password si es creación
      if (!user) {
        dataToSave.password = formData.password;
      }

      await onSave(dataToSave, user ? "PATCH" : "POST");
      setConfirmPassword("");
      setShowPasswordConfirm(false);
      setPasswordError("");
      onClose();
    } catch (error) {
      setError(t('modal.errors.save'));
      console.error("Error saving user:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!user) return;
    setIsDeleting(true);
    try {
      await onDelete(user.id);
      onClose();
    } catch (error) {
      setError(t('modal.errors.delete'));
      console.error("Error deleting user:", error);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={
        isOpen ? "user-mgmt-modal-overlay" : "user-mgmt-modal-overlay hidden"
      }
    >
      <div className="user-mgmt-modal">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Modal de advertencia para eliminar usuario */}
        {showDeleteModal && (
          <div className="user-mgmt-modal-overlay delete-modal-overlay">
            <div
              className="user-mgmt-modal delete-modal"
              style={{
                maxWidth: 380,
                textAlign: "center",
                padding: 32,
                position: "relative",
              }}
            >
              {/* Botón X para cerrar */}
              <ButtonClose
                onClick={() => setShowDeleteModal(false)}
                ariaLabel={t('modal.delete.cancel')}
                className="delete-modal-close"
              />
              <div className="delete-modal-title">
                {t('modal.delete.title', { name: (formData.name || user?.name || user?.username) })}
              </div>
              <div className="delete-modal-warning">
                {t('modal.delete.warning')}<br />
                {t('modal.delete.confirm')}
              </div>
              <div className="delete-modal-actions">
                <ButtonCancel
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeleting}
                  ariaLabel={t('modal.delete.cancel')}
                />
                <ButtonDelete
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                  ariaLabel={t('modal.delete.delete')}
                >
                  {isDeleting ? t('modal.delete.deleting') : t('modal.delete.delete')}
                </ButtonDelete>
              </div>
            </div>
          </div>
        )}
        {/* Modal Header */}
        <div className="user-mgmt-modal-header">
          <div className="user-mgmt-modal-title-section">
            <UserIcon size={20} aria-hidden="true" stroke="var(--primary)" />
            <h2 className="user-mgmt-modal-title">
              {user ? t('modal.title.edit') : t('modal.title.create')}
            </h2>
          </div>
          {!showDeleteModal && (
            <ButtonClose
              onClick={onClose}
              ariaLabel={t('modal.delete.cancel')}
              disabled={isSubmitting || isDeleting}
            />
          )}
        </div>

        {/* Modal Content */}
        <form onSubmit={handleSubmit}>
          <div className="form-custom-user">
            {/* Name Field */}
            <div className="item-custom">
              <label className="label-custom">{t('modal.fields.fullName')}</label>
              <input
                type="text"
                value={formData.name ?? ""}
                onChange={(e) => handleFormChange("name", e.target.value)}
                className="user-mgmt-filter-select"
                placeholder={t('modal.placeholders.fullName')}
                required
                disabled={isSubmitting || isDeleting}
              />
            </div>

            {/* Username y Email solo en creación */}
            {!user && (
              <>
                <div className="item-custom">
                  <label className="label-custom">{t('modal.fields.username')}</label>
                  <input
                    type="text"
                    value={formData.username ?? ""}
                    onChange={(e) =>
                      handleFormChange("username", e.target.value)
                    }
                    className="user-mgmt-filter-select"
                    placeholder={t('modal.placeholders.username')}
                    required
                    disabled={isSubmitting || isDeleting}
                  />
                </div>
                <div className="item-custom">
                  <label className="label-custom">{t('modal.fields.email')}</label>
                  <input
                    type="email"
                    value={formData.email ?? ""}
                    onChange={(e) => handleFormChange("email", e.target.value)}
                    className="user-mgmt-filter-select"
                    placeholder={t('modal.placeholders.email')}
                    required
                    disabled={isSubmitting || isDeleting}
                  />
                </div>
                <div className="item-custom">
                  <label className="label-custom">{t('modal.fields.password')}</label>
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password ?? ""}
                      onChange={(e) =>
                        handleFormChange("password", e.target.value)
                      }
                      className="user-mgmt-filter-select"
                      placeholder={t('modal.placeholders.password')}
                      required
                      disabled={isSubmitting || isDeleting}
                      style={{ paddingRight: 36 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      tabIndex={-1}
                      className="password-toggle"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div className="item-custom">
                  <label className="label-custom">{t('modal.fields.confirmPassword')}</label>
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type={showPasswordConfirm ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="user-mgmt-filter-select"
                      placeholder={t('modal.placeholders.confirmPassword')}
                      required
                      disabled={isSubmitting || isDeleting}
                      style={{ paddingRight: 36 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswordConfirm((prev) => !prev)}
                      tabIndex={-1}
                      className="password-toggle"
                    >
                      {showPasswordConfirm ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                </div>
                {passwordError && (
                  <div className="password-error">
                    {passwordError}
                  </div>
                )}
              </>
            )}

            {/* Fecha de alta (solo edición) */}
            {user && (
              <div className="item-custom item-inline">
                <label className="label-custom join-date-label">
                  {t('modal.fields.joinDate')}
                  {formData.date_joined
                    ? new Date(formData.date_joined).toLocaleDateString()
                    : ""}
                </label>
              </div>
            )}

            {/* Organización Field */}
            <div className="item-custom">
              <label className="label-custom">{t('modal.fields.organization')}</label>
              <input
                type="text"
                className="user-mgmt-filter-select"
                value={currentUserOrganization?.name || "Aulasneo"}
                readOnly
                disabled
              />
            </div>


            {/* Role Field */}
            <div className="item-custom">
              <div className="item-custom item-inline" style={{ justifyContent: "space-between"}}>
                <label className="label-custom">{t('modal.fields.role')}</label>

                {/* Credit Limit Field (solo lectura) */}
                <div className="item-custom item-inline">
                  <label className="label-custom credit-limit-label">
                    {t('modal.fields.creditLimit')}{" "}
                    {(() => {
                      const val =
                        creditLimits?.[formData.role_id] ??
                        creditLimits?.[String(formData.role_id)];
                      return val === null ? 'Unlimited' : (val != null ? val : "0");
                    })()}
                  </label>
                </div>
              </div>

              <Combobox
                value={formData.role_id}
                options={roleOptions}
                onChange={(val) => handleFormChange("role_id", val)}
                label={t('modal.fields.role')}
                className="combobox-full"
              />
            </div>



            {/* is_staff Field - Commented out: Admin access now handled through role.have_admin_access */}
            {/* <div className="item-custom">
              <label className="label-custom">{t('modal.fields.staffRole')}</label>
              <Combobox
                value={formData.is_staff ? "admin" : "student"}
                options={[
                  { value: "admin", label: t('modal.options.role.admin') },
                  { value: "student", label: t('modal.options.role.student') },
                ]}
                onChange={(val) =>
                  handleFormChange("is_staff", val === "admin")
                }
                label={t('modal.fields.staffRole')}
                disabled={isSubmitting || isDeleting}
                className="combobox-full"
              />
            </div> */}

            {/* Activo Field (checkbox toggle) */}
            <div className="item-custom">
              <label className="label-custom">{t('modal.fields.state')}</label>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <input
                  type="checkbox"
                  id="user-active-checkbox"
                  checked={!!formData.is_active}
                  onChange={(e) => handleFormChange("is_active", e.target.checked)}
                  disabled={isSubmitting || isDeleting}
                  style={{ width: 18, height: 18 }}
                />
                <label htmlFor="user-active-checkbox" className="label-custom" style={{ marginBottom: 0 }}>
                  {formData.is_active
                    ? t('modal.options.state.active')
                    : t('modal.options.state.inactive')}
                </label>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="modal-footer-reorganized">
            {/* Cancel and Save buttons */}
            <div className="actions-footer">
              <ButtonCancel
                onClick={onClose}
                ariaLabel={t('modal.delete.cancel')}
                disabled={isSubmitting || isDeleting}
              />
              <ButtonSave
                type="submit"
                ariaLabel={user ? t('common:buttons.update') : t('common:buttons.save')}
                isEdit={!!user}
                isLoading={isSubmitting}
                disabled={
                  isSubmitting ||
                  isDeleting ||
                  !formData.name ||
                  !formData.email
                }
              />
            </div>
            
            {/* Delete button - full width at bottom */}
            {user && (
              <div className="delete-button-full-width">
                <ButtonDelete
                  onClick={handleDelete}
                  disabled={isSubmitting || isDeleting}
                  className="delete-button-full"
                >
                  {isDeleting ? t('modal.delete.deleting') : t('modal.delete.delete')}
                </ButtonDelete>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
